import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  ListingTableProps,
  ActionOption,
  ActionsMenuProps,
} from "./listing-table-props";
import "./list-table.scss";
import RenderCellContent from "../mui-table/render-cell-content";
import ActionsMenu from "./actions-menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton } from "@mui/material";

const ListingTable: React.FC<ListingTableProps> = ({
  data,
  headers,
  onRowClick,
  noResultsText = "No data found",
  fieldName,
  handleAction,
  actionWidth,
}) => {
  const getTextContent = (value: any): string | React.ReactNode => {
    if (!value) return "-";

    if (typeof value === "string") {
      return (
        <span
          className={`table-cell-content 
        ${fieldName === "assignment-rules"
              ? value === "Active"
                ? "active-color"
                : value === "Inactive"
                  ? "inActive-color"
                  : ""
              : ""
            }`}
          title={value}
        >
          {value}
        </span>
      );
    }

    if (React.isValidElement(value)) {
      const { props }: any = value;
      if (props.status || typeof value.type === "function") {
        return (
          <span
            className="table-cell-content clickable"
            title={props.status || props.children}
          >
            {value}
          </span>
        );
      }

      // For clickable React elements
      if (
        (props.onClick || typeof value.type === "function") &&
        !props.status
      ) {
        return (
          <span
            className="table-cell-content clickable"
            title={props.children || "Click"}
          >
            {value}
          </span>
        );
      }

      // Handle mixed children (text + React elements)
      if (Array.isArray(props.children)) {
        return (
          <span
            className="table-cell-content"
            title={props.children
              .map((child: any) => (typeof child === "string" ? child : ""))
              .join(" ")}
          >
            {props.children.map((child: any, index: number) =>
              typeof child === "string" ? child : child
            )}
          </span>
        );
      }

      //To handle Link
      if (
        React.isValidElement(value) &&
        typeof value.type === "object" &&
        value.type !== null &&
        "$$typeof" in value.type &&
        (value.type as any).$$typeof.toString() ===
        "Symbol(react.forward_ref)" &&
        ((value.type as any).render?.name === "LinkWithRef" ||
          (value.type as any).render?.displayName === "Link")
      ) {
        const getLinkText = (node: React.ReactNode): string | number => {
          if (typeof node === "string" || typeof node === "number") return node;
          if (Array?.isArray(node)) return node?.map(getLinkText)?.join(" ");
          if (React?.isValidElement(node)) {
            const element = node as React.ReactElement<any>;
            return getLinkText(element?.props?.children);
          }
          return "";
        };

        const element = value as React.ReactElement<any>;
        const linkText = getLinkText(element.props.children);
        return (
          <span
            className="table-cell-content clickable"
            title={linkText.toString()}
          >
            {value}
          </span>
        );
      }

      //To handle Span

      if (React.isValidElement(value) && value.type === "span") {
        const element = value as React.ReactElement<any>;
        const className = element.props?.className || "";
        const children = element.props?.children;

        if (className.includes("leads-link") && typeof children === "string") {
          return (
            <span
              className="table-cell-content clickable"
              title={children.toString()}
            >
              {value}
            </span>
          );
        }
      }

      return value;
    }

    if (typeof value === "number") {
      return (
        <span className="table-cell-content" title={value.toString()}>
          {value}
        </span>
      );
    }

    return "-";
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<any>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {

    if (!row?.actionOptions || row.actionOptions.length === 0) {
      return;
    }
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handleMenuOptionClick = (option: ActionOption) => {
    if (handleAction) {
      handleAction(option?.label, menuRow?.id, menuRow);
    }
    handleClose();
  };

  return (
    <TableContainer
      component={Paper}
      className="listing-table-container"
      style={{ padding: "0 !important" }}
    >
      <Table stickyHeader className="custom-listing-table">
        <TableHead>
          <TableRow>
            {headers.map((header) => (
              <TableCell
                key={header.key}
                align={header.align || "left"}
                className="listing-table-header-cell"
                style={(header as any).width ? { width: (header as any).width } : {}}
              >
                {header.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length > 0 ? (
            data.map((row, index) => (
              <TableRow
                key={index}
                onClick={() => onRowClick && onRowClick(row)}
                className={`
                ${onRowClick ? "clickable-row" : ""}
                ${row?.__isInactive ? "inactive-row" : ""}
              `}
              >
                {headers.map((header) => (
                  <TableCell
                    key={header.key}
                    align={header.align || "left"}
                    className="listing-table-cell"
                    style={(header as any).width ? { width: (header as any).width } : {}}
                  >
                    {header.key === "action" &&
                      row[header.key] === "verticalEllipse" ? (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuClick(e, row);
                        }}
                        className="vertical-ellipse-icon"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    ) : (
                      <span
                        className={`${header?.isOnClick ? "table-cell-pointer" : ""}`}
                      >
                        <RenderCellContent
                          text={
                            header?.valueGetter
                              ? header?.valueGetter(row)
                              : getTextContent(row[header?.key])
                          }
                        />
                      </span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={headers.length} className="no-results-cell">
                {noResultsText}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <ActionsMenu
        anchorEl={anchorEl}
        onClose={handleClose}
        onOptionClick={handleMenuOptionClick}
        options={menuRow?.actionOptions || []}
        width={actionWidth}
      />
    </TableContainer>
  );
};

export default ListingTable;
